// components/Forms/addArticleForm.jsx
import { useState, useEffect } from "react";
import { createArticle, updateArticle } from "../../api/articleApi";
import {
  getAllArticlesTag,
  createArticlesTag,
  deleteTag,
} from "../../api/articlesTagApi";
import { uploadArticleImage } from "../../api/articleApi";
import ArticleInlineEditor from "../articleInlineEditor";
import { buildSelfContainedHtml } from "../article-editor";

const AddArticleForm = ({
  setRefresh,
  setLoading,
  editData = null,
  onCancelEdit = null,
}) => {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [content, setContent] = useState("");
  const [readTime, setReadTime] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [articleId, setArticleId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [allTags, setAllTags] = useState([]);
  const [mainTag, setMainTag] = useState("");
  const [newTagName, setNewTagName] = useState("");
  const [showNewTagInput, setShowNewTagInput] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await getAllArticlesTag();
        setAllTags(res.data);
      } catch (err) {
        console.error("خطا در دریافت تگ‌ها:", err);
      }
    };
    fetchTags();
  }, []);

  useEffect(() => {
    if (editData) {
      setTitle(editData.title || "");
      setSummary(editData.summary || "");
      setContent(editData.content || "");
      setReadTime(editData.readTime || "");
      setImageUrl(editData.imageUrl || "");
      setMainTag(editData.mainTag?._id || editData.mainTag || "");
      setKeywords(editData.tags || []); // اگر tags آرایه‌ای از رشته‌هاست
      setArticleId(editData._id);
      setIsEdit(true);
    } else {
      setTitle("");
      setSummary("");
      setContent("");
      setReadTime("");
      setImageUrl("");
      setMainTag("");
      setKeywords([]);
      setArticleId(null);
      setIsEdit(false);
    }
  }, [editData]);

  const handleCreateTag = async () => {
    const trimmed = newTagName.trim();
    if (!trimmed) {
      alert("لطفاً نام تگ را وارد کنید.");
      return;
    }
    try {
      const res = await createArticlesTag({ tagName: trimmed });
      const newTag = res.data.newArticle;
      setAllTags([...allTags, newTag]);
      setNewTagName("");
      setShowNewTagInput(false);
    } catch (err) {
      console.error("خطا در ایجاد تگ:", err);
      alert("خطا در ایجاد تگ. لطفاً دوباره تلاش کنید.");
    }
  };

  const handleDeleteTag = async (tagId, tagName) => {
    const confirmMessage = `آیا از حذف تگ "${tagName}" مطمئن هستید؟\n\n⚠️ این تگ برای همیشه پاک می‌شود و پست‌های مرتبط با این تگ هیچ‌وقت نمایش داده نمی‌شوند.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await deleteTag(tagId);
      setAllTags(allTags.filter((tag) => tag._id !== tagId));
      if (mainTag === tagId) setMainTag("");
    } catch (err) {
      console.error("خطا در حذف تگ:", err);
      alert("خطا در حذف تگ. لطفاً دوباره تلاش کنید.");
    }
  };

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (!trimmed) {
      alert("لطفاً کلمه کلیدی را وارد کنید.");
      return;
    }
    if (keywords.includes(trimmed)) {
      alert("این کلمه کلیدی قبلاً اضافه شده است.");
      return;
    }
    setKeywords([...keywords, trimmed]);
    setKeywordInput("");
  };

  const handleRemoveKeyword = (keyword) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const maxSize = 500 * 1024;
    if (file.size > maxSize) {
      alert("حجم عکس باید کمتر از 500 کیلو بایت باشه");
      e.target.value = "";
      return;
    }
    setUploadingImage(true);
    try {
      const res = await uploadArticleImage(file);
      if (res.status === 200) {
        alert("آپلود عکس موفقیت آمیز بود");
      } else if (res.status === 400) {
        alert(res.message);
      }
      setImageUrl(res.data.url);
    } catch (err) {
      console.error("خطا در آپلود تصویر:", err);
      alert("خطا در آپلود تصویر. لطفاً دوباره تلاش کنید.");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // اعتبارسنجی تک‌تک فیلدها با پیام جداگانه
    if (!title.trim()) {
      alert("لطفاً عنوان مقاله را وارد کنید.");
      return;
    }
    if (title.trim().length > 300) {
      alert("عنوان مقاله نمی‌تواند بیشتر از ۳۰۰ کاراکتر باشد.");
      return;
    }
    if (!summary.trim()) {
      alert("لطفاً خلاصه مقاله را وارد کنید.");
      return;
    }
    if (summary.trim().length > 500) {
      alert("خلاصه مقاله نمی‌تواند بیشتر از ۵۰۰ کاراکتر باشد.");
      return;
    }
    if (!content.trim()) {
      alert("لطفاً محتوای مقاله را وارد کنید.");
      return;
    }
    if (!mainTag) {
      alert("لطفاً تگ اصلی مقاله را انتخاب کنید.");
      return;
    }
    if (!readTime.trim()) {
      alert("لطفاً مدت زمان مطالعه را وارد کنید ..");
      return;
    }
    if (!imageUrl) {
      alert("لطفاً عکس پس زمینه را وارد کنید .");
      return;
    }
    if (!keywords) {
      alert("لطفاً کلمات کلیدی را وارد کنید .");
      return;
    }

    setSubmitting(true);
    setLoading(true);

    try {
      let res;
      const data = {
        title,
        summary,
        content,
        readTime,
        imageUrl,
        mainTag,
        tags: keywords,
        id: articleId,
      };
      if (isEdit && articleId) {
        res = await updateArticle(data);
        if (res.status === 200) {
          alert("مقاله با موفقیت ویرایش شد.");
        } else {
          alert("ویرایش مقالهشکست خورد");
        }
      } else {
        res = await createArticle(data);
        if (res.status === 201) {
          alert("مقاله با موفقیت ویرایش شد.");
        } else {
          alert("ساخت مقاله شکست خورد");
        }
      }

      // ریست فرم
      setTitle("");
      setSummary("");
      setContent("");
      setReadTime("");
      setImageUrl("");
      setMainTag("");
      setKeywords([]);
      setArticleId(null);
      setIsEdit(false);
      setRefresh((prev) => !prev);
      if (onCancelEdit) onCancelEdit();
    } catch (error) {
      console.error("Error saving article:", error);
      alert("خطا در ذخیره مقاله. لطفاً دوباره تلاش کنید.");
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  // ---------- رندر ----------
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* عنوان */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          عنوان مقاله
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
          placeholder="عنوان مقاله را وارد کنید"
          maxLength={300}
          required
        />
        {title && (
          <p className="text-xs text-gray-400 mt-1">
            {title.length}/{300} کاراکتر
          </p>
        )}
      </div>

      {/* خلاصه */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          خلاصه مقاله
        </label>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows="2"
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
          placeholder="خلاصه مقاله را وارد کنید"
          maxLength={500}
          required
        />
        {summary && (
          <p className="text-xs text-gray-400 mt-1">
            {summary.length}/{500} کاراکتر
          </p>
        )}
      </div>

      {/* زمان مطالعه */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          زمان مطالعه (دقیقه)
        </label>
        <input
          type="text"
          value={readTime}
          onChange={(e) => setReadTime(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
          placeholder="مثلاً 5"
          min="1"
        />
      </div>

      {/* آپلود تصویر */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          تصویر شاخص مقاله
        </label>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploadingImage}
            className="text-sm"
          />
          {uploadingImage && (
            <span className="text-sm text-blue-600">در حال آپلود...</span>
          )}
        </div>
        {imageUrl && (
          <div className="mt-2">
            <img
              src={imageUrl}
              alt="پیش‌نمایش"
              className="h-16 w-auto object-cover rounded-lg border border-gray-300"
            />
          </div>
        )}
      </div>

      {/* محتوا (ادیتور) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          محتوای مقاله
        </label>
        <ArticleInlineEditor choosedContent={content} setContent={setContent} />
      </div>

      {/* ---------- انتخاب و ایجاد تگ اصلی ---------- */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          تگ اصلی مقاله <span className="text-red-500">*</span>
        </label>

        {/* نمایش لیست تگ‌ها */}
        {allTags.length === 0 ? (
          <p className="text-sm text-gray-500">هیچ تگی یافت نشد.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {allTags.map((tag) => {
              const isSelected = mainTag === tag._id;
              return (
                <div
                  key={tag._id}
                  className="flex items-center gap-1 bg-white border border-gray-300 rounded-full pr-1 hover:border-blue-300 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setMainTag(tag._id)}
                    className={`
                      px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                      ${
                        isSelected
                          ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-300 ring-offset-1"
                          : "text-gray-700 hover:bg-blue-50"
                      }
                    `}
                  >
                    {tag.tagName}
                    {isSelected && <span className="mr-1 text-white">✓</span>}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteTag(tag._id, tag.tagName)}
                    className="text-red-400 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-50"
                    title="حذف تگ"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {mainTag && (
          <p className="mt-2 text-xs text-green-600">
            تگ انتخاب‌شده: {allTags.find((t) => t._id === mainTag)?.tagName}
          </p>
        )}

        {/* بخش ایجاد تگ جدید */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setShowNewTagInput(!showNewTagInput)}
            className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showNewTagInput ? "− بستن" : "+ ساخت تگ اصلی جدید"}
          </button>

          {showNewTagInput && (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="نام تگ جدید را وارد کنید"
              />
              <button
                type="button"
                onClick={handleCreateTag}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                ایجاد
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ---------- بخش کلمات کلیدی ---------- */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          کلمات کلیدی (Tags)
        </label>
        <p className="text-xs text-gray-500 mb-3">
          کلمات کلیدی را وارد کرده و روی افزودن کلیک کنید.
        </p>

        <div className="flex gap-2">
          <input
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            placeholder="مثلاً: آموزش، برنامه‌نویسی، React"
          />
          <button
            type="button"
            onClick={handleAddKeyword}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            افزودن
          </button>
        </div>

        {/* نمایش کلمات کلیدی اضافه‌شده */}
        {keywords.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {keywords.map((keyword, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
              >
                {keyword}
                <button
                  type="button"
                  onClick={() => handleRemoveKeyword(keyword)}
                  className="text-red-500 hover:text-red-700 font-bold"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* دکمه‌های ارسال */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting
            ? "در حال ذخیره..."
            : isEdit
              ? "ویرایش مقاله"
              : "افزودن مقاله"}
        </button>
        {isEdit && onCancelEdit && (
          <button
            type="button"
            onClick={onCancelEdit}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            انصراف
          </button>
        )}
      </div>
    </form>
  );
};

export default AddArticleForm;
